import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { ApplicationUserService } from '../utils/application-user.service';
import { IRoleScope } from '../interfaces/role.scope.interface';

export interface PendingDocumentReviewCheckResponse {
  username: string;
  hasDocumentsToReview: boolean;
  trainingByRole: UserTrainingByRoleVideoResponse[];
}

export interface UserTrainingByRoleVideoResponse
{
    role: string;
    video: string;
    scopes: UserTrainingScopeResponse[];
}

export interface UserTrainingScopeResponse
{
    organizationId: string;
    studyId: string;
}

@Injectable({
  providedIn: 'root',
})
export class PostLoginChecksService {
  private hasRunForCurrentSession = false;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly httpClient: HttpClient,
    private readonly applicationUserService: ApplicationUserService,
  ) {}

  public tryRunAfterNavigation(
    urlAfterRedirects: string,
    forceRefresh = false,
  ): Observable<PendingDocumentReviewCheckResponse | null> {
    if (!this.authService.userIsLogged()) {
      this.hasRunForCurrentSession = false;
      return of(null);
    }

    if (this.isLoginRoute(urlAfterRedirects)) {
      this.hasRunForCurrentSession = false;
      return of(null);
    }

    if (!forceRefresh && this.hasRunForCurrentSession) {
      return of(null);
    }

    if (!this.applicationUserService?.loggedUser) {
      return of(null);
    }

    return this.getPendingDocumentReviewCheck().pipe(
      tap(() => {
        this.hasRunForCurrentSession = true;
      }),
      map((response) => {
        if (!response || response.username !== this.applicationUserService.loggedUser.name) {
          return null;
        }

        const hasTrainingByRole = Array.isArray(response.trainingByRole) && response.trainingByRole.length > 0;
        if (hasTrainingByRole || !!response.hasDocumentsToReview) {
          return response;
        }

        return null;
      }),
      catchError((error) => {
        this.hasRunForCurrentSession = false;
        return throwError(() => error);
      }),
    );
  }

  private getPendingDocumentReviewCheck(): Observable<PendingDocumentReviewCheckResponse> {
    let pendingReviewEndpointPath = `/dsmb/user/${this.applicationUserService.loggedUser?.name}/documents/review-check`;
    return this.httpClient.get<PendingDocumentReviewCheckResponse>(`${environment.dsmbHost}${pendingReviewEndpointPath}`);
  }

  private isLoginRoute(urlAfterRedirects: string): boolean {
    const url = urlAfterRedirects || this.router.url || '';
    return url.startsWith('/login');
  }
}